import { useDisclosure } from "@chakra-ui/hooks";
import React, { useCallback, useMemo } from "react";
import {
  createQColumnHelper,
  DataProvider,
  QBox,
  QButton,
  QCard,
  QCenter,
  QDataTable,
  QIconButton,
  QLookup,
  QStack,
  QTag,
  QText,
  QTooltip,
} from "@qualio/ui-components";
import {
  FeatureFlags,
  isDesignElementFMEARisk,
  isDesignElementISORisk,
  LinkedElement,
} from "@design-controls/types";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { ItemStatusTag } from "../ItemStatusTag/ItemStatusTag";
import { useNavigate } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";

const columnHelper = createQColumnHelper<LinkedElement>();

type LinkedDesignElement = LinkedElement & {
  latest?: string;
  version?: string;
};

type Props = {
  label: string;
  "data-cy"?: string;
  data: Array<LinkedDesignElement>;
  onChange: (items: LinkedDesignElement[]) => void;
  value: LinkedDesignElement[];
  remainingSelections?: number;
};

export const ReviewDesignElement: React.FC<Props> = ({
  label,
  "data-cy": dataCy,
  data,
  onChange,
  value,
  remainingSelections,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const configs = useCurrentConfigs();
  const navigate = useNavigate();
  const features = useFlags();

  const defaultIds = useMemo(() => value.map((item) => item.id), [value]);

  const isPreselected = useCallback(
    (result: LinkedElement) => {
      return !!defaultIds.find((id) => id === result.id);
    },
    [defaultIds],
  );

  const onSelect = useCallback(
    (selection: readonly LinkedElement[]) => {
      // If there's a remaining selections limit, only take what's allowed
      let finalSelection = [...selection];
      if (remainingSelections !== undefined && remainingSelections >= 0) {
        // Only allow adding as many items as remaining slots
        const maxNewItems = remainingSelections + value.length;
        finalSelection = finalSelection.slice(0, maxNewItems);
      }
      onChange(finalSelection);
      onClose();
      return Promise.resolve();
    },
    [onChange, onClose, remainingSelections, value.length],
  );

  const onDeleteItem = useCallback(
    (item: LinkedElement) => {
      onChange(value.filter((element) => element.id !== item.id));
    },
    [value, onChange],
  );

  const onRefreshItem = useCallback(
    (item: LinkedDesignElement) => {
      if (item.latest && item.latest !== item.version) {
        onChange(
          value.map((element) => {
            if (element.id !== item.id) {
              return element;
            }

            return { ...element, version: item.latest };
          }),
        );
      }
    },
    [value, onChange],
  );

  const onClickToDiff = useCallback(
    (item: LinkedElement) => {
      return () => navigate(`${item.id}/${item.version}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.text("code", {
        header: t("lookup.header.id"),
        maxWidth: "100px",
      }),
      columnHelper.text("title", {
        header: t("lookup.header.title"),
        maxWidth: "400px",
      }),
      {
        header: "Type",
        meta: {
          maxWidth: "150px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: LinkedDesignElement };
        }) => {
          let label = (
            configs.find((config) => config.type === row.type) as
              | { label: string }
              | undefined
          )?.label;

          if (isDesignElementISORisk(row)) {
            label = t("risks.iso.tabTitle");
          }

          if (isDesignElementFMEARisk(row)) {
            label = t("risks.fmea.tabTitle");
          }

          return <QTag variantColor="gray">{label}</QTag>;
        },
      },
      {
        header: t("designElements.lookup.header.status"),
        id: "status",
        meta: {
          maxWidth: "100px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: LinkedDesignElement };
        }) => {
          return (
            row.itemStatus && <ItemStatusTag itemStatus={row.itemStatus} />
          );
        },
      },
      {
        header: " ",
        id: "latest",
        meta: {
          maxWidth: "48px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: LinkedDesignElement };
        }) => {
          return row.latest && row.latest !== row.version ? (
            <QIconButton
              iconName="RefreshCw"
              aria-label={t("common.ariaLabels.updateToLatestVersion")}
              data-cy={`refresh-${row.id}`}
              onClick={() => onRefreshItem(row)}
            />
          ) : null;
        },
      },
    ];

    if (features[FeatureFlags.COMPARE_VIEW]) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          maxWidth: "50px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: LinkedElement };
        }) => {
          return (
            row.itemStatus !== "NEW" && (
              <QCenter>
                <QTooltip label={t("common.ariaLabels.seeDifference")}>
                  <QBox>
                    <QIconButton
                      iconName="GitPullRequest"
                      aria-label={t(
                        "common.ariaLabels.seeDifferenceBetweenVersion",
                      )}
                      onClick={onClickToDiff(row)}
                    />
                  </QBox>
                </QTooltip>
              </QCenter>
            )
          );
        },
      });
    }

    columns.push({
      header: " ",
      meta: {
        maxWidth: "48px",
      },
      cell: ({
        row: { original: row },
      }: {
        row: { original: LinkedDesignElement };
      }) => {
        return (
          <QIconButton
            iconName="Trash"
            aria-label={t("common.ariaLabels.remove")}
            data-cy={`remove-${row.id}`}
            onClick={() => onDeleteItem(row)}
          />
        );
      },
    });

    return columns;
  }, [configs, onDeleteItem, onRefreshItem, onClickToDiff, features, t]);

  const filterOption = useCallback(
    (item: Readonly<LinkedDesignElement>, searchTerm: string | undefined) => {
      if (!searchTerm) {
        return true;
      }

      return [item.title, item.code].some((field) =>
        field.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      );
    },
    [],
  );

  return (
    <>
      <QCard>
        <QStack padding={4} gap={4}>
          <QStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <QText fontSize="sm" fontWeight={600}>
              {label}
            </QText>
            <QButton variant="link" onClick={onOpen} data-cy={dataCy}>
              Add {label.toLocaleLowerCase()}
            </QButton>
          </QStack>
          {value.length > 0 && (
            <DataProvider.Fixed data={value} isLoading={false}>
              <QDataTable columns={columns} withHeaders={false} hideItemCount />
            </DataProvider.Fixed>
          )}
        </QStack>
      </QCard>
      {isOpen && (
        <DataProvider.Fixed<LinkedElement>
          data={data}
          filterOption={filterOption}
        >
          <QLookup.MultiSelect
            isOpen={isOpen}
            onCancel={onClose}
            onSelect={onSelect}
            accessors={{ id: "id" }}
            view={ELEMENT_VIEW(t)}
            isItemPreSelected={isPreselected}
            data-cy={`${dataCy}-lookup`}
          />
        </DataProvider.Fixed>
      )}
    </>
  );
};

const ELEMENT_VIEW = (t: any) =>
  ({
    code: {
      header: t("lookup.header.id"),
      width: "",
      render: (code) => <QText>{code}</QText>,
    },
    title: {
      header: t("lookup.header.title"),
      width: "75%",
      render: (title) => <QText>{title}</QText>,
    },
    itemStatus: {
      header: t("designElements.lookup.header.status"),
      width: "100px",
      render: (itemStatus) =>
        itemStatus && <ItemStatusTag itemStatus={itemStatus} />,
    },
  }) as QLookup.DataView<LinkedElement>;
